import { useTheme } from "../theme/ThemeProvider"
import type { PlatformHealth, DegradedBanner } from "../types"
import { DegradedNotice } from "./DegradedNotice"

type StatusBeaconProps = {
  health?: PlatformHealth | null
  loading?: boolean
  fallback?: Partial<Record<"stripe" | "twilio" | "notifications" | "subscriptions", string>>
  banner?: DegradedBanner | null
}

type RailStatus = {
  label: string
  state: string
  message?: string | null
}

export function StatusBeacon({ health, loading, fallback, banner }: StatusBeaconProps) {
  const { theme } = useTheme()
  const isFairy = theme === "fairy"

  const baseRails: Array<{ key: "twilio" | "stripe" | "subscriptions" | "notifications"; label: string }> = [
    { key: "twilio", label: "Twilio" },
    { key: "stripe", label: "Stripe" },
  ]
  if (health?.subscriptions || fallback?.subscriptions) {
    baseRails.push({ key: "subscriptions", label: "Subscriptions" })
  }
  if (health?.notifications || fallback?.notifications) {
    baseRails.push({ key: "notifications", label: "Notifications" })
  }

  const rails: RailStatus[] = baseRails.map((entry) => {
    const metrics =
      entry.key === "twilio"
        ? health?.twilio
        : entry.key === "stripe"
          ? health?.stripe
          : entry.key === "subscriptions"
            ? health?.subscriptions
            : health?.notifications
    const state = metrics?.state ?? fallback?.[entry.key] ?? "unknown"
    return {
      label: entry.label,
      state,
      message: metrics?.message ?? null,
    }
  })

  const ariaLabel = rails.map((rail) => `${rail.label} ${rail.state}`).join(", ")
  const background = isFairy ? "rgba(244,255,251,0.92)" : "rgba(18,18,20,0.62)"
  const textColor = isFairy ? "#1f2d42" : "var(--text)"
  const border = isFairy ? "1px solid rgba(46,214,181,0.4)" : "1px solid rgba(255,255,255,0.12)"

  return (
    <div style={{ display: "grid", gap: "8px" }}>
      <div
        className="mono small"
        style={{
          display: "inline-flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "12px",
          padding: "10px 14px",
          background,
          letterSpacing: "0.14em",
          color: textColor,
          border,
          minHeight: "40px",
        }}
        aria-label={loading ? "Loading platform health" : ariaLabel}
      >
        {loading ? <span>Loading…</span> : rails.map((rail) => <BeaconDot key={rail.label} rail={rail} />)}
      </div>
      {banner?.state === "degraded" ? (
        <DegradedNotice banner={banner} persona={isFairy ? "fairy" : "demon"} align="inline" />
      ) : null}
    </div>
  )
}

function BeaconDot({ rail }: { rail: RailStatus }) {
  const palette: Record<string, string> = {
    ok: "#31d0aa",
    degraded: "#facc15",
    issue: "#ff7a1a",
    down: "#ff2b4f",
    unknown: "#71717a",
  }
  const tone = palette[rail.state.toLowerCase()] ?? palette.unknown
  const statusLabel = rail.state.toUpperCase()
  const tooltipParts = [statusLabel]
  if (rail.message) tooltipParts.push(rail.message)

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }} title={tooltipParts.join(" · ")}>
      <span
        aria-hidden
        style={{
          width: "8px",
          height: "8px",
          border: `1px solid ${tone}`,
          background: tone,
          boxShadow: `0 0 12px ${tone}`,
        }}
      />
      <span>{rail.label}</span>
      <span style={{ color: tone, textTransform: "uppercase" }}>{statusLabel}</span>
    </span>
  )
}

export default StatusBeacon
